import { Controller, type Path, type UseFormReturn } from "react-hook-form";
import type { IzCampaignFormBrand } from "../../../z/brand/campaign-form.brand.z";
import { FormRange } from "../core/range.core";
import Input from "../core/input.core";
import StarIcon from "../../../assets/svgs/common/star.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../core/tooltip.core";
import { Info } from "lucide-react";
import { ThreeDWrapper } from "../core/3d-wrapper.core";

type BudgetCardProps = {
  form: UseFormReturn<IzCampaignFormBrand>;
  name: Path<IzCampaignFormBrand>;
  label: string;
  required?: boolean;
  maxBudget: number;
  setMaxBudget: (value: number) => void;
  description?: string;
  placeholder?: string;
  valueType?: "decimal" | "integer" | "percentage"; // Add value type prop
  decimalPlaces?: number; // Number of decimal places
  step?: number; // Step size for slider
  minValue?: number; // Minimum value
  label1?: string;
  value1?: string;
  label2?: string;
  value2?: string;
  note?: string;
};

const BudgetCardWhite = ({
  form,
  label,
  name,
  required = false,
  maxBudget,
  setMaxBudget,
  description,
  placeholder,
  valueType = "integer", // Default to integer
  decimalPlaces = 2, // Default 2 decimal places
  step, // Auto-calculated if not provided
  minValue, // Auto-calculated if not provided
  label1,
  value1,
  label2,
  value2,
  note,
}: BudgetCardProps) => {
  // Auto-calculate step and min based on valueType
  const getStepValue = () => {
    if (step !== undefined) return step;

    switch (valueType) {
      case "decimal":
        return 0.01; // Fine control for decimals
      case "percentage":
        return 0.1; // 0.1% increments
      default:
        return 1; // Whole numbers
    }
  };

  const getMinValue = () => {
    if (minValue !== undefined) return minValue;

    switch (valueType) {
      case "decimal":
        return 0.01;
      case "percentage":
        return 0;
      default:
        return 0;
    }
  };

  // Format display value based on type
  const formatValue = (value: number) => {
    if (valueType === "percentage") {
      return `${value.toFixed(decimalPlaces)}%`;
    }
    if (valueType === "decimal") {
      return `₹${value.toFixed(decimalPlaces)}`;
    }
    // Integer
    return `₹${Math.round(value)}`;
  };

  // Parse input value based on type
  const parseInputValue = (inputValue: string) => {
    const cleanValue = inputValue.replace(/[₹%,]/g, "");
    const parsed = Number.parseFloat(cleanValue);

    if (Number.isNaN(parsed)) return 0;

    if (valueType === "integer") {
      return Math.round(parsed);
    }

    return Number.parseFloat(parsed.toFixed(decimalPlaces));
  };

  const calculatedStep = getStepValue();
  const calculatedMin =  getMinValue();

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-1">
        {label}
        {required && <StarIcon />}
      </div>

      <div className="flex flex-col p-8 border border-black rounded-xl w-full font-cabinetgrotesk">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-20 w-3/5">
            <Controller
              name={name}
              control={form.control}
              render={({ field }) => (
                <Input
                  label=""
                  disabled
                  className="hidden -mt-1 p-5"
                  placeholder={placeholder}
                  error={
                    form.formState.errors[
                      name as keyof typeof form.formState.errors
                    ]?.message
                  }
                  variant="default"
                  type="number"
                  fullWidth
                  required
                  step={calculatedStep}
                  {...field}
                  value={
                    typeof field.value === "number" ||
                    typeof field.value === "string"
                      ? field.value
                      : ""
                  }
                  onChange={(e) => {
                    const value = parseInputValue(e.target.value);
                    setMaxBudget(value);
                    field.onChange(value);
                    form.setValue(name, value);
                  }}
                />
              )}
            />

            <div className="flex flex-col gap-2 w-full">
              <FormRange
                form={form}
                name={name}
                max={maxBudget}
                min={calculatedMin}
                step={calculatedStep}
                decimalPlaces={decimalPlaces}
                onValueChange={(value) => form.setValue(name, value)}
                className="bg-gray-100 rounded-xl"
                valueType={valueType}
              />
              <div className="flex justify-between items-center w-full font-semibold text-[18px]">
                <span>{formatValue(calculatedMin)}</span>
                <span>
                  {formatValue(
                    Number(form.getValues(name)) > maxBudget
                      ? Number(form.getValues(name)?.toString()) || 0
                      : maxBudget
                  )}
                </span>
              </div>
            </div>
          </div>
          <hr className="bg-secondary opacity-30 w-1/5 rotate-90" />
          <div className="flex flex-col gap-8 w-2/5 font-bold">
            <div className="flex flex-col items-start">
              <span className="font-normal text-lg text-secondary">
                {label1}
              </span>
              <p className="text-4xl">{value1}</p>
            </div>
            <div className="flex flex-col items-start">
              <span className="flex items-center gap-1 font-normal text-lg text-secondary">
                {label2}
                {note && (
                  <Tooltip>
                    <TooltipTrigger type="button">
                    <Info width={15} height={15}/>
                    </TooltipTrigger>
                    <TooltipContent className="bg-transparent shadow-none">
                      <ThreeDWrapper
                        hover={false}
                        className="flex justify-center items-center p-2 rounded-lg w-64 text-black"
                      >{note}
                      </ThreeDWrapper>
                      
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
              <p className="text-4xl">{value2}</p>
            </div>
          </div>
        </div>
        <div className="opacity-15 pt-10 border-secondary border-b w-full" />
        <div className="py-4">{description}</div>
      </div>
    </div>
  );
};

export default BudgetCardWhite;
